import { FC } from 'react';
import {
  Stack,
  Button,
  Group,
  Text,
  Image,
  Paper,
  Badge,
  SimpleGrid,
  ActionIcon,
  Box,
} from '@mantine/core';
import { IconPlus, IconCheck, IconX } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { ScannedPage } from './hooks/useDocumentScanner';
import classes from './DocumentScanner.module.css';

interface ScanSummaryProps {
  pages: ScannedPage[];
  onRemovePage: (pageId: string) => void;
  onAddMorePages: () => void;
  onFinalize: () => void;
  isProcessing?: boolean;
}

export const ScanSummary: FC<ScanSummaryProps> = ({
  pages,
  onRemovePage,
  onAddMorePages,
  onFinalize,
  isProcessing = false,
}) => {
  const { t } = useTranslation(['common']);

  const pageCount = pages.length;

  return (
    <Stack gap="md">
      {/* Header */}
      <Group gap="sm" justify="center">
        <Text size="lg" fw={500}>
          {t('documentScanner.summaryTitle', {
            defaultValue: 'Review Your Pages',
          })}
        </Text>
        {pageCount > 0 && (
          <Badge size="lg" variant="filled" color="blue">
            {pageCount}{' '}
            {pageCount === 1
              ? t('documentScanner.pageSingular', { defaultValue: 'page' })
              : t('documentScanner.pagesPlural', { defaultValue: 'pages' })}
          </Badge>
        )}
      </Group>

      {/* Thumbnail Grid */}
      {pageCount === 0 ? (
        <Paper withBorder p="xl" radius="md" className={classes.emptyState}>
          <Stack align="center" gap="md">
            <Text c="dimmed" ta="center">
              {t('documentScanner.emptyBatch', {
                defaultValue: 'No pages scanned yet',
              })}
            </Text>
            <Button
              variant="light"
              leftSection={<IconPlus size={18} />}
              onClick={onAddMorePages}
            >
              {t('documentScanner.scanFirstPage', {
                defaultValue: 'Scan First Page',
              })}
            </Button>
          </Stack>
        </Paper>
      ) : (
        <SimpleGrid
          cols={{ base: 2, sm: 3, md: 4 }}
          spacing="md"
          className={classes.thumbnailGrid}
        >
          {pages.map((page, index) => (
            <Paper
              key={page.id}
              withBorder
              radius="md"
              p={0}
              className={classes.thumbnailCard}
            >
              <Box className={classes.thumbnailWrapper}>
                {/* Delete button */}
                <ActionIcon
                  size="sm"
                  variant="filled"
                  color="red"
                  className={classes.deleteButton}
                  onClick={() => onRemovePage(page.id)}
                  aria-label={t('documentScanner.removePage', {
                    defaultValue: 'Remove page',
                  })}
                >
                  <IconX size={14} />
                </ActionIcon>

                {/* Page number badge */}
                <Badge
                  size="sm"
                  variant="filled"
                  color="dark"
                  className={classes.pageNumber}
                >
                  {index + 1}
                </Badge>

                {/* Thumbnail image */}
                <Image
                  src={page.processedImage}
                  alt={`Page ${index + 1}`}
                  fit="cover"
                  className={classes.thumbnailImage}
                />
              </Box>
            </Paper>
          ))}
        </SimpleGrid>
      )}

      {/* Action buttons */}
      <Group gap="md" justify="center" className={classes.summaryActions}>
        <Button
          variant="light"
          leftSection={<IconPlus size={18} />}
          onClick={onAddMorePages}
          disabled={isProcessing}
        >
          {t('documentScanner.addMorePages', {
            defaultValue: 'Add More Pages',
          })}
        </Button>

        <Button
          leftSection={<IconCheck size={18} />}
          onClick={onFinalize}
          loading={isProcessing}
          disabled={pageCount === 0}
        >
          {t('documentScanner.createPdf', {
            defaultValue: 'Create PDF',
          })}{' '}
          {pageCount > 0 && `(${pageCount})`}
        </Button>
      </Group>
    </Stack>
  );
};

export default ScanSummary;
